/** Google tag destinations. Loaded under Consent Mode, denied by default (see CookieConsentBanner). */
export const GOOGLE_ANALYTICS_ID = 'G-ZNX31ETZTP';
export const GOOGLE_ADS_ID = 'AW-18460016317';

/** "Sign-up" conversion action in Google Ads. */
export const GOOGLE_ADS_SIGNUP_CONVERSION = `${GOOGLE_ADS_ID}/USolCJPRmIAdEL39teJE`;

/** "Demo request" conversion action (Book appointment goal) in Google Ads. */
export const GOOGLE_ADS_DEMO_REQUEST_CONVERSION = `${GOOGLE_ADS_ID}/P-8PCI3osYkdEL39teJE`;

// Accounts older than this are returning users who never finished the
// onboarding tour, not fresh sign-ups.
const SIGNUP_WINDOW_MS = 24 * 60 * 60 * 1000;

type SignupCandidate = {
  id?: string;
  type?: string;
  createdAt?: string;
  onboardingCompletedAt?: string | null;
};

/** True for a business owner whose account was created within the last day. */
export const isFreshSignup = (user: SignupCandidate | undefined, now = Date.now()) => {
  if (!user?.id || user.type !== 'BUSINESS_OWNER' || user.onboardingCompletedAt) return false;

  const createdAt = user.createdAt ? Date.parse(user.createdAt) : NaN;
  return Number.isFinite(createdAt) && now - createdAt < SIGNUP_WINDOW_MS;
};

/**
 * Reports a sign-up to Google Ads. With consent, the user id is the transaction
 * id, so Google Ads counts each account once even if this fires on several
 * visits or devices. Without consent the id is withheld and Consent Mode sends
 * a cookieless ping that Google uses only for modeled conversions.
 * Returns false when the Google tag is not initialized yet.
 */
export const sendSignupConversion = (userId: string, { consented }: { consented: boolean }) => {
  if (typeof window === 'undefined' || !window.gtag) return false;

  window.gtag('event', 'conversion', {
    send_to: GOOGLE_ADS_SIGNUP_CONVERSION,
    value: 1.0,
    currency: 'USD',
    ...(consented ? { transaction_id: userId } : {}),
  });
  return true;
};

/**
 * Reports a successfully submitted /demo request to Google Ads. There is no
 * stable id to deduplicate on; the conversion action counts one conversion per
 * ad click. Consent Mode decides whether cookies back the hit.
 * Returns false when the Google tag is not initialized yet.
 */
export const sendDemoRequestConversion = () => {
  if (typeof window === 'undefined' || !window.gtag) return false;

  window.gtag('event', 'conversion', {
    send_to: GOOGLE_ADS_DEMO_REQUEST_CONVERSION,
    value: 1.0,
    currency: 'USD',
  });
  return true;
};
